import React from 'react';
import { useDispatch } from 'react-redux';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from '@tkww/the-bash-frontend';

import getProfileServiceTypeAndLocationText from 'helpers/profiles/getProfileServiceTypeAndLocationText';
import { gmConfig } from 'helpers/config';
import {
  getProfileStartingAtPriceRangeDetails,
  getProfileReviewsDetails,
  getProfileBookingsDetails,
} from 'helpers/vendor/details';
import { ProfileDetails } from 'views/Profile';
import { Image } from 'components/Image/Image';
import { REQUEST_QUOTE_TEXT } from 'constants/Vendors';
import { profileReviewsRequestQuote } from 'state/modules/analytics/profile';
import getProfileFeedbackHeroImages from 'helpers/getProfileFeedbackHeroImages';
import { PROFILE_SUMMARY_THUMBNAIL_SIZE_DESKTOP } from 'constants/Profiles';
import getSxAttributes from 'helpers/getSxAttributes';
import sxPropType from 'propTypes/sx';

const types = Object.freeze({
  default: 'default',
  widget: 'widget',
});

const StyledButton = styled(Button)(({ theme }) => ({
  whiteSpace: 'nowrap',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    width: 'auto',
    maxWidth: 250,
  },
}));

const ProfileSummary = React.forwardRef((props, ref) => {
  const { profile, type, sxDetails, sxDetailsList } = props;
  const dispatch = useDispatch();
  const { profileName, thumbnailUrl, id } = profile;

  const handleContactClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(
      profileReviewsRequestQuote('Profile Reviews Vendor Summary', REQUEST_QUOTE_TEXT, profile)
    );
    window.location.assign(`https://${gmConfig.domains.www}/requestquote?memberId=${id}`);
  };

  const getProfileDetails = () => {
    const details = [];
    const reviewDetails = getProfileReviewsDetails(profile, { mr: 1.5 });
    if (!isEmpty(reviewDetails)) {
      details.push(reviewDetails);
    }

    const profileLocationDetails = () => <>{getProfileServiceTypeAndLocationText(profile)}</>;
    details.push({ icon: () => <></>, detail: profileLocationDetails });

    const verifiedBookingDetails = getProfileBookingsDetails(profile);
    if (!isEmpty(verifiedBookingDetails)) {
      details.push(verifiedBookingDetails);
    }

    const startingAtPriceDetails = getProfileStartingAtPriceRangeDetails(profile);
    if (!isEmpty(startingAtPriceDetails)) {
      details.push(startingAtPriceDetails);
    }

    return details;
  };

  const thumbnailImages = getProfileFeedbackHeroImages(thumbnailUrl);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {type !== types.widget && (
          <Image
            lazyLoad={false}
            srcMobile={thumbnailImages.mobile}
            src={thumbnailImages.desktop}
            alt={`${profileName}, profile image`}
            sxPicture={(theme) => ({
              margin: 0,
              marginBottom: 4,
              width: '100%',
              minHeight: `calc(100vw - calc(${theme.spacing(2)} * 2))`,
              textAlign: 'center',
              [theme.breakpoints.up('sm')]: {
                minHeight: `calc(100vw - calc(${theme.spacing(3)} * 2))`,
              },
              [theme.breakpoints.up('md')]: {
                minHeight: 'auto',
                marginRight: 4,
                textAlign: 'left',
                marginBottom: 1,
              },
            })}
            sx={(theme) => ({
              width: '100%',
              height: '100%',
              borderRadius: '3px',
              [theme.breakpoints.up('md')]: {
                width: PROFILE_SUMMARY_THUMBNAIL_SIZE_DESKTOP,
                height: PROFILE_SUMMARY_THUMBNAIL_SIZE_DESKTOP,
              },
            })}
          />
        )}
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 2,
            width: { md: '100%' },
            padding: { md: '0 16px 0 0' },
            ...getSxAttributes(theme, sxDetails),
          })}
        >
          <ProfileDetails
            details={getProfileDetails()}
            sx={(theme) => ({ marginBottom: '14px', ...getSxAttributes(theme, sxDetailsList) })}
          />
          {type !== types.widget && (
            <div ref={ref}>
              <StyledButton onClick={handleContactClick}>{REQUEST_QUOTE_TEXT}</StyledButton>
            </div>
          )}
        </Box>
      </Box>
    </>
  );
});

ProfileSummary.propTypes = {
  profile: PropTypes.shape({}).isRequired,
  type: PropTypes.oneOf(Object.values(types)),
  sxDetails: sxPropType,
  sxDetailsList: sxPropType,
};

ProfileSummary.defaultProps = {
  type: types.default,
  sxDetails: {},
  sxDetailsList: {},
};

export default ProfileSummary;
